import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  InspirationInitModel,
  InspirationInitVariables,
  InspirationModel,
  InspirationVariables,
} from '../../../../models/email-template/inspiration.interface';

/**
 * Mockup-styled Inspiration + 2x Photo block - a drop-in replacement for
 * app-inspiration in block-renderer-v2 (same [block]/[isPreview] inputs and
 * outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation),
 * app-field-editor-v2 and app-pin sub-components and the exact same
 * InspirationVariables field names - only the surrounding layout is new.
 * Like x2-experiences-v2 but without the shared intro paragraph or a
 * per-card description field, and heading/CTA are centered rather than
 * left-aligned (matches v1/the mockup's "no intro" variant exactly).
 */
@Component({
  selector: 'app-inspiration-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './inspiration-v2.component.html',
  styleUrl: './inspiration-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InspirationComponentV2 extends BaseComponent implements OnInit {
  @Input() block: InspirationModel = new InspirationInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new InspirationInitVariables();
    }
  }

  image1Placeholder = 'First Image';
  image2Placeholder = 'Second Image';
  image1HrefPlaceholder = 'Enter First Image Redirect Link';
  image2HrefPlaceholder = 'Enter Second Image Redirect Link';
  image1AltPlaceholder = 'Enter First Image Alt Text';
  image2AltPlaceholder = 'Enter Second Image Alt Text';
  heading1Placeholder = 'Enter First Heading';
  heading2Placeholder = 'Enter Second Heading';
  ctaLink1Placeholder = 'Enter First CTA Link';
  ctaLink2Placeholder = 'Enter Second CTA Link';
  ctaText1Placeholder = 'Enter First CTA Text';
  ctaText2Placeholder = 'Enter Second CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected image1AsUpload = true;
  protected image2AsUpload = true;

  protected toggleImage1Mode(): void {
    this.image1AsUpload = !this.image1AsUpload;
  }

  protected toggleImage2Mode(): void {
    this.image2AsUpload = !this.image2AsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get card1ImageHref(): string {
    return this.hrefFor(this.block.variables?.['Inspiration1ImageHref'], this.block.variables?.['Inspiration1ImageHrefSegmentRegex']);
  }

  get card2ImageHref(): string {
    return this.hrefFor(this.block.variables?.['Inspiration2ImageHref'], this.block.variables?.['Inspiration2ImageHrefSegmentRegex']);
  }

  get card1CtaHref(): string {
    return this.hrefFor(this.block.variables?.['Inspiration1CtaLink'], this.block.variables?.['Inspiration1CtaLinkSegmentRegex']);
  }

  get card2CtaHref(): string {
    return this.hrefFor(this.block.variables?.['Inspiration2CtaLink'], this.block.variables?.['Inspiration2CtaLinkSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof InspirationVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
